import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Box, 
  Typography, 
  AppBar, 
  Toolbar, 
  Container,
  Paper,
  Grid,
  Card,
  CardContent,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tabs,
  Tab,
  Button,
  Badge,
  Backdrop,
  CircularProgress
} from '@mui/material';
import {
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  FitnessCenter as FitnessIcon,
  EventNote as EventIcon,
  AccountCircle as AccountIcon,
  Badge as MembershipIcon,
  Notifications as NotificationsIcon
} from '@mui/icons-material';
import { getCurrentUser, logout } from '../../services/api';
import tuttoGymLogo from '../../images/tuttogym.jpeg';
import LanguageSwitcher from '../../components/LanguageSwitcher';

const CustomerDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [settingsMenuAnchor, setSettingsMenuAnchor] = useState(null);
  const [loggingOut, setLoggingOut] = useState(false);

  useEffect(() => {
    // Get current user data
    const user = getCurrentUser();
    if (!user || !user.email) {
      console.error('No user data found');
      navigate('/panel');
      return;
    }
    
    // Check if user has the correct role to access this dashboard
    if (user.role !== 'customer') {
      console.warn('Unauthorized access attempt to customer dashboard by', user.role);
      
      // Redirect user to their appropriate dashboard
      switch(user.role) {
        case 'company':
          navigate('/panel/company-dashboard');
          break;
        case 'partner':
          navigate('/panel/partner-dashboard');
          break;
        default:
          navigate('/panel');
          break;
      }
      return;
    }
    
    setCurrentUser(user);
  }, [navigate]);

  const handleLogout = async () => {
    try {
      setLoggingOut(true);
      // Close the menu if it exists
      if (typeof handleSettingsMenuClose === 'function') {
        handleSettingsMenuClose();
      }
      
      await logout();
      
      // Add a delay for the animation
      setTimeout(() => {
        setLoggingOut(false);
        // Redirect to main page instead of panel
        window.location.replace('/');
      }, 1000);
    } catch (error) {
      console.error('Logout failed:', error);
      setLoggingOut(false);
    }
  };

  const handleSettingsMenuOpen = (event) => {
    setSettingsMenuAnchor(event.currentTarget);
  };

  const handleSettingsMenuClose = () => {
    setSettingsMenuAnchor(null);
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Logout Animation Backdrop */}
      <Backdrop
        sx={{ 
          color: '#fff', 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          flexDirection: 'column',
          gap: 2
        }}
        open={loggingOut}
      >
        <CircularProgress color="inherit" />
        <Typography variant="h6">{t('auth.loggingOut', 'Logging out...')}</Typography>
      </Backdrop>
      
      {/* App Bar with title and user info */}
      <AppBar position="static" color="primary" elevation={0}>
        <Toolbar>
          {/* Logo */}
          <Box
            component="img"
            src={tuttoGymLogo}
            alt="TuttoGym Logo"
            sx={{ height: 40, mr: 2 }}
          />
          
          {/* Dashboard title with user name */}
          <Typography variant="h6" component="div">
            {currentUser?.firstName} {currentUser?.lastName} - {t('dashboard.customer.title', 'Customer Dashboard')}
          </Typography>
          
          <Box sx={{ flexGrow: 1 }} />
          
          {/* Language selector */}
          <LanguageSwitcher />
          
          {/* User email */}
          {currentUser && (
            <Typography variant="body2" sx={{ mx: 2 }}>
              {currentUser.email}
            </Typography>
          )}
          
          {/* Logout button */}
          <IconButton 
            color="inherit" 
            onClick={handleSettingsMenuOpen}
            size="large"
            title={t('common.logout', 'Logout')}
          >
            <SettingsIcon />
          </IconButton>
          
          <Menu
            anchorEl={settingsMenuAnchor}
            open={Boolean(settingsMenuAnchor)}
            onClose={handleSettingsMenuClose}
          >
            <MenuItem onClick={handleLogout} disabled={loggingOut}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('common.logout', 'Logout')}</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, flexGrow: 1 }}>
        <Grid container spacing={3}>
          {/* User Profile Card */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Avatar 
                sx={{ 
                  width: 100, 
                  height: 100, 
                  margin: '0 auto', 
                  bgcolor: 'primary.main',
                  fontSize: '2rem'
                }}
              >
                {currentUser?.firstName?.charAt(0) || 'U'}
              </Avatar>
              <Typography variant="h5" sx={{ mt: 2 }}>
                {currentUser?.firstName} {currentUser?.lastName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {currentUser?.email}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('dashboard.customer.id', 'Customer ID')}: {currentUser?.id}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('dashboard.memberSince', 'Member since')}: {new Date().toLocaleDateString()}
              </Typography>
            </Paper>
            
            {/* Navigation */}
            <Paper sx={{ mt: 3 }}>
              <List>
                <ListItem button>
                  <ListItemIcon>
                    <FitnessIcon />
                  </ListItemIcon>
                  <ListItemText primary={t('dashboard.customer.myFitnessCenters', 'My Fitness Centers')} />
                </ListItem>
                <Divider />
                <ListItem button>
                  <ListItemIcon>
                    <EventIcon />
                  </ListItemIcon>
                  <ListItemText primary={t('dashboard.customer.myActivities', 'My Activities')} />
                </ListItem>
                <Divider />
                <ListItem button>
                  <ListItemIcon>
                    <AccountIcon />
                  </ListItemIcon>
                  <ListItemText primary={t('dashboard.history', 'History')} />
                </ListItem>
                <Divider />
                <ListItem button>
                  <ListItemIcon>
                    <MembershipIcon />
                  </ListItemIcon>
                  <ListItemText primary={t('dashboard.statistics', 'Statistics')} />
                </ListItem>
              </List>
            </Paper>
          </Grid>
          
          {/* Main Content */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={selectedTab} onChange={handleTabChange}>
                  <Tab label={t('dashboard.tabs.dashboard', 'Dashboard')} />
                  <Tab label={t('dashboard.tabs.activity', 'Activity')} />
                  <Tab label={t('dashboard.tabs.settings', 'Settings')} />
                </Tabs>
              </Box>
              
              {selectedTab === 0 && (
                <Box sx={{ p: 2 }}>
                  <Typography variant="h5" gutterBottom>
                    {t('dashboard.customer.welcome', 'Welcome')}, {currentUser?.firstName}!
                  </Typography>
                  
                  <Typography paragraph>
                    {t('dashboard.customer.welcomeText', 'This is your personal TuttoGym dashboard. Here you can track your fitness activities, manage your memberships, and view your progress.')}
                  </Typography>
                  
                  <Grid container spacing={2} sx={{ mt: 2 }}>
                    <Grid item xs={12} sm={6}>
                      <Card>
                        <CardContent>
                          <Typography color="text.secondary" gutterBottom>
                            {t('dashboard.customer.activitiesThisMonth', 'Activities This Month')}
                          </Typography>
                          <Typography variant="h3">
                            0
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Card>
                        <CardContent>
                          <Typography color="text.secondary" gutterBottom>
                            {t('dashboard.customer.activeMemberships', 'Active Memberships')}
                          </Typography>
                          <Typography variant="h3">
                            0
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              )}
              
              {selectedTab === 1 && (
                <Box sx={{ p: 2 }}>
                  <Typography variant="h5" gutterBottom>
                    {t('dashboard.customer.yourActivity', 'Your Activity')}
                  </Typography>
                  <Typography paragraph>
                    {t('dashboard.customer.activityDescription', 'Your recent activities and check-ins will appear here.')}
                  </Typography>
                </Box>
              )}
              
              {selectedTab === 2 && (
                <Box sx={{ p: 2 }}>
                  <Typography variant="h5" gutterBottom>
                    {t('dashboard.settings.title', 'Account Settings')}
                  </Typography>
                  <Typography paragraph>
                    {t('dashboard.settings.description', 'Manage your account settings, preferences and notifications.')}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default CustomerDashboard; 